import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import * as auth from "../_redux/authRedux";
import { useDispatch } from "react-redux";
import {
  appVersion,
  baseUrl,
  branchUrl,
  config,
} from "../../../../services/config";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

const initialValues = { email: "", password: "" };

function Login(props) {
  const dispatch = useDispatch();
  const { intl } = props;
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email("Wrong email format")
      .min(3, "Minimum 3 characters")
      .max(50, "Maximum 50 characters")
      .required("Email is required"),
    password: Yup.string()
      .min(3, "Minimum 3 characters")
      .max(50, "Maximum 50 characters")
      .required("Password is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: LoginSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      setStatus("");
      setServerError("");

      fetch(`${baseUrl}/login`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: values.email,
          password: values.password,
        }),
      })
        .then((resp) => resp.json())
        .then((user) => {
          if (user.metadata.success === true) {
            if (user.payload?.user?.login_type !== "system_login") {
              setStatus("Invalid User Type");
              setLoading(false);
              setSubmitting(false);
              return;
            }
            localStorage.setItem(
              "userDetails",
              JSON.stringify(user.payload.user),
            );
            localStorage.setItem("userToken", user.payload.token);
            localStorage.setItem(
              "session_expires_at",
              String(Date.now() + 45 * 60 * 1000),
            );
            localStorage.setItem("appVersion", appVersion);
            localStorage.setItem("branchUrl", branchUrl);
            setLoading(false);
            dispatch({
              type: "ADD_USER",
              payload: {
                isAuthenticated: true,
                authtoken: user.payload.token,
                user: user.payload.user,
              },
            });
          } else {
            setStatus(
              intl.formatMessage({ id: "AUTH.VALIDATION.INVALID_LOGIN" }),
            );
            setLoading(false);
            setSubmitting(false);
          }
        })
        .catch((err) => {
          console.log(err);
          setServerError("Something went wrong. Please try again.");
          setLoading(false);
          setSubmitting(false);
        });
    },
  });

  // ── Shared input style ──────────────────────────────────────────────────────
  const inputStyle = (fieldName) => ({
    width: "100%",
    border: `1.5px solid ${
      formik.touched[fieldName] && formik.errors[fieldName]
        ? "#e63946"
        : formik.touched[fieldName]
        ? "#27ae60"
        : "#e2e8f0"
    }`,
    borderRadius: 10,
    padding: "13px 44px",
    fontSize: 15,
    color: "#0f172a",
    background: "#f8fafc",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s ease",
    fontFamily: "inherit",
  });

  const iconStyle = {
    position: "absolute",
    left: 14,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#94a3b8",
    fontSize: 20,
    pointerEvents: "none",
  };

  return (
    <div
      className="auth-login-card"
      style={{
        background: "#fff",
        borderRadius: 18,
        padding: "44px 40px 36px",
        boxShadow: "0 20px 60px rgba(15,23,42,0.12)",
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <h2
          style={{
            color: "#0f172a",
            fontWeight: 800,
            fontSize: 26,
            margin: "0 0 8px",
            letterSpacing: "-0.5px",
          }}
        >
          Sign In
        </h2>
        <p style={{ color: "#64748b", fontSize: 14, margin: 0 }}>
          Enter your credentials to access your account
        </p>
      </div>

      {/* Status / error banner */}
      {(formik.status || serverError) && (
        <div
          style={{
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 22,
            color: "#c9293a",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {formik.status || serverError}
        </div>
      )}

      <form onSubmit={formik.handleSubmit} noValidate>
        {/* ── Email ── */}
        <div style={{ marginBottom: 18 }}>
          <label
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 700,
              color: "#374151",
              marginBottom: 7,
            }}
          >
            Email Address
          </label>
          <div style={{ position: "relative" }}>
            <MailOutlineIcon style={iconStyle} />
            <input
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              style={inputStyle("email")}
              {...formik.getFieldProps("email")}
            />
          </div>
          {formik.touched.email && formik.errors.email && (
            <p
              style={{
                color: "#e63946",
                fontSize: 12,
                margin: "5px 0 0",
                fontWeight: 600,
              }}
            >
              {formik.errors.email}
            </p>
          )}
        </div>

        {/* ── Password ── */}
        <div style={{ marginBottom: 28 }}>
          <label
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 700,
              color: "#374151",
              marginBottom: 7,
            }}
          >
            Password
          </label>
          <div style={{ position: "relative" }}>
            <LockOutlinedIcon style={iconStyle} />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              autoComplete="current-password"
              style={{ ...inputStyle("password"), paddingRight: 48 }}
              {...formik.getFieldProps("password")}
            />
            {/* Eye toggle */}
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              style={{
                position: "absolute",
                right: 14,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                color: "#94a3b8",
                display: "flex",
                alignItems: "center",
                lineHeight: 1,
              }}
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <VisibilityOff style={{ fontSize: 20 }} />
              ) : (
                <Visibility style={{ fontSize: 20 }} />
              )}
            </button>
          </div>
          {formik.touched.password && formik.errors.password && (
            <p
              style={{
                color: "#e63946",
                fontSize: 12,
                margin: "5px 0 0",
                fontWeight: 600,
              }}
            >
              {formik.errors.password}
            </p>
          )}
        </div>

        {/* ── Submit ── */}
        <button
          type="submit"
          disabled={formik.isSubmitting || loading}
          style={{
            width: "100%",
            background:
              formik.isSubmitting || loading
                ? "#94a3b8"
                : `linear-gradient(135deg, ${config?.btnColor} 0%, ${config?.sideBarHoverColor} 100%)`,
            color: "#fff",
            border: "none",
            borderRadius: 10,
            padding: "15px 24px",
            fontSize: 16,
            fontWeight: 700,
            cursor: formik.isSubmitting || loading ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            letterSpacing: 0.3,
            fontFamily: "inherit",
            transition: "background 0.2s ease",
          }}
        >
          {loading ? (
            <>
              <span
                style={{
                  width: 18,
                  height: 18,
                  border: "2px solid rgba(255,255,255,0.35)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  display: "inline-block",
                  animation: "auth-spin 0.75s linear infinite",
                }}
              />
              Signing in…
            </>
          ) : (
            "Sign In"
          )}
        </button>
      </form>

      <style>{`
        @keyframes auth-spin { to { transform: rotate(360deg); } }
        @media (max-width: 575px) {
          .auth-login-card { padding: 30px 22px 26px !important; border-radius: 14px !important; }
        }
      `}</style>
    </div>
  );
}

export default injectIntl(connect(null, auth.actions)(Login));
